<template>
    <div>
        <div class="columns">
            <div class="column">
                <h2 class="title is-3 between-center">
                    <span>
                        <span>Change Orders</span>
                        <a href="#" class="gear" @click.prevent="isTableSettingsModalActive = true">
                            <o-icon pack="fa" icon="gear" size="is-small" />
                        </a>
                    </span>
                </h2>
            </div>
            <div class="column">
                <div class="field is-grouped is-pulled-right">
                    <p v-if="isDeleteSelectedButtonVisible" class="control">
                        <a class="button is-danger" @click.prevent="confirmCustomDelete">Delete selected</a>
                    </p>
                    <p class="control">
                        <router-link :to="{ name: 'change-orders.create', query: { project_id: currentProjectId } }"
                            class="button is-info">
                            New
                        </router-link>
                    </p>
                </div>
            </div>
        </div>
        <o-table v-if="isTableVisible" class="project-width-table is-responsive is-responsive-xs-desktop"
            :data="changeOrders" :bordered="table.bordered" :striped="table.striped" :narrowed="table.narrowed"
            :checkable="table.checkable" :mobile-cards="table.mobileCards" :paginated="table.paginated"
            :pagination-simple="table.paginationSimple" :per-page="table.perPage" :default-sort="['created_at', 'desc']"
            v-model:selected="selected" :checked-rows="checkedRows" @update:checkedRows="$event => checkedRows = $event">
            <template>
                <template v-for="col in tableColumns" :key="col.field">
                    <o-table-column v-slot="{ row }" :field="col.field"
                        :label="col.field === 'actions' ? '' : col.label" :sortable="col.sortable">
                        <router-link v-if="col.field === 'last_editor_id'"
                            :to="{ name: 'users.show', params: { slug: row.last_editor_id } }">
                            {{ userNameById(row.last_editor_id) }}
                        </router-link>

                        <router-link v-if="col.field === 'number'"
                            :to="{ name: 'change-orders.edit', params: { slug: row.id, project, proposal: row } }">
                            {{ row.number }}
                        </router-link>

                        <span v-if="col.field === 'status'">{{ getChangeOrderStatus(row) }}</span>
                        <span v-if="col.field === 'status_updated_at'">
                            {{ $filters.valueOrDefault($filters.datetime(row.status_updated_at), '-') }}
                        </span>
                        <router-link v-if="col.field === 'created_by'"
                            :to="{ name: 'users.show', params: { slug: row.created_by } }">
                            {{ userNameById(row.created_by) }}
                        </router-link>
                        <span v-if="col.field === 'created_at'">{{ $filters.datetime(row.created_at) }}</span>
                        <span v-if="col.field === 'updated_at'">{{ $filters.datetime(row.updated_at) }}</span>

                        <span v-if="col.field === 'floor_number'">{{ $filters.valueOrDefault(row.floor_number) }}</span>
                        <span v-if="col.field === 'company'">{{ companyNameById(row.company_id) }}</span>
                        <span v-if="col.field === 'contact'">{{ contactNameById(row.contact_id) }}</span>
                        <span v-if="col.field === 'approval_deadline'">{{
                            $filters.valueOrDefault($filters.date(row.approval_deadline)) }}</span>
                        <span v-if="col.field === 'notes'">{{ row.notes }}</span>
                        <span v-if="col.field === 'deposit_percent'">{{ row.deposit_percent }}</span>
                        <span v-if="col.field === 'pay_in_full'">{{ $filters.yesNo(row.pay_in_full) }}</span>
                        <span v-if="col.field === 'sent_at'">{{ $filters.valueOrDefault($filters.datetime(row.sent_at),
                            '–') }}</span>

                        <o-dropdown v-if="col.field === 'actions'" hoverable position="bottom-left"
                            class="table-actions">
                            <template #trigger>
                                <button class="button">
                                    <o-icon pack="fa" icon="ellipsis-v" />
                                </button>
                            </template>
                            <o-dropdown-item has-link>
                                <router-link
                                    :to="{ name: 'change-orders.edit', params: { slug: row.id, project, proposal: row } }">
                                    Edit
                                </router-link>
                            </o-dropdown-item>
                            <o-dropdown-item has-link>
                                <a :href="`/proposals/${row.id}/pdf`" @click.prevent="openPdfPreviewModal(row)">Preview
                                    PDF</a>
                            </o-dropdown-item>
                            <o-dropdown-item has-link>
                                <a :href="`/change-orders/${row.id}/pdf?dl=0'`" target="_blank">Download PDF</a>
                            </o-dropdown-item>
                            <o-dropdown-item has-link>
                                <a
                                    @click="openMessageModal({ entity: row, entity_type: 'change-order', context: 'change-order-message' })">Send
                                    to
                                    Customer</a>
                            </o-dropdown-item>
                            <o-dropdown-item has-link>
                                <a @click="openReminderModal(row.id)">Set Reminder</a>
                            </o-dropdown-item>
                            <o-dropdown-item has-link>
                                <a @click="openSignatureStatusModal(row.id)">Signature status</a>
                            </o-dropdown-item>
                            <o-dropdown-item v-if="row.status === 'draft'" has-link>
                                <a @click.prevent="handleDecline(row)">Decline</a>
                            </o-dropdown-item>
                            <o-dropdown-item v-if="row.status === 'draft' || 'sent'" has-link>
                                <a @click.prevent="handleApprove(row)">Approve</a>
                            </o-dropdown-item>
                        </o-dropdown>
                    </o-table-column>
                </template>
            </template>

            <template v-slot:empty>
<div  class="has-text-centered">
                This table is empty!
            </div>
</template>
        </o-table>

        <o-modal v-model:active="isReminderModalActive" :can-cancel="['escape', 'outside', 'button']" scroll="keep"
            has-modal-card>
            <reminder-modal :reference-id="changeOrderId" reference-type="change-order" />
        </o-modal>

        <o-modal v-model:active="isSignatureStatusModalActive" :can-cancel="['escape', 'outside', 'button']"
            scroll="keep" has-modal-card>
            <signatures-modal :reference-id="changeOrderId" reference-type="change-order" />
        </o-modal>

        <o-modal v-model:active="isPdfPreviewActive" :can-cancel="['escape', 'outside', 'button']" scroll="keep"
            has-modal-card :width="960">
            <preview-modal :file="currentPreviewFile" />
        </o-modal>

        <o-modal v-model:active="isTableSettingsModalActive" :can-cancel="['escape', 'outside', 'button']" scroll="keep"
            has-modal-card class="table-settings-modal">
            <table-settings-modal :slug="tableName" />
        </o-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Table from '../../../mixins/Table'
import ChangeRemindBy from './../../../components/modals/ChangeRemindBy'
import PreviewModal from './../../../components/modals/Preview'
import { getChangeOrderStatus } from './../../../store/modules/change-orders/utils'

export default {

    components: { PreviewModal },
    mixins: [Table],

    data() {
        return {
            tableName: 'change-orders',
            isTableSettingsModalActive: false,
            isReminderModalActive: false,
            isSignatureStatusModalActive: false,
            project: null,
            currentChangeOrder: null,
            isPdfPreviewActive: false,
            currentPreviewFile: null,
            changeOrderId: null
        }
    },

    mounted() {
        this.fetchChangeOrders({ project_id: this.$route.params.slug }).then(() => {
            _.forEach(this.changeOrders, c => this.validateRemindBy(c))
        })
        this.project = this.projectById(this.$route.params.slug)
    },

    computed: {
        ...mapGetters([
            'changeOrdersByProjectId',
            'projectById',
            'userNameById',
            'currentProjectId',
            'contactNameById',
            'companyNameById',
            'currentUserId'
        ]),

        changeOrders() {
            return this.changeOrdersByProjectId(this.currentProjectId).reverse()
        }
    },

    methods: {
        ...mapActions({
            removeItem: 'removeChangeOrder'
        }),

        ...mapActions([
            'approveChangeOrder',
            'declineChangeOrder',
            'fetchChangeOrders',
            'openMessageModal',
            'removeRemindBy',
            'clearProjectServices'
        ]),

        getChangeOrderStatus,

        handleApprove(co) {
            this.confirmMessage(
                'Are you sure?',
                'This action cannot be undone.',
                'Yes'
            ).then(() => {
                this.approveChangeOrder(co)
                    .then(res => {
                        this.clearProjectServices()
                        this.successMessage('Change Order approved')
                        return res
                    })
            })
        },

        handleDecline(co) {
            this.confirmMessage(
                'Are you sure?',
                'This action cannot be undone.',
                'Yes'
            ).then(() => {
                this.declineChangeOrder(co)
                    .then(res => {
                        this.successMessage('Change Order declined')
                        return res
                    })
            })
        },

        openPdfPreviewModal(changeOrder) {
            const port = window.location.port ? `:${window.location.port}` : '';
            this.currentPreviewFile = {
                name: `Change Order #${changeOrder.number}`,
                mime: 'application/pdf',
                path: `${window.location.protocol}//${window.location.hostname}${port}/change-orders/${changeOrder.id}/pdf`,
                generatedOnTheFly: true
            }
            this.isPdfPreviewActive = true
        },
        openReminderModal(changeOrderId) {
            this.isReminderModalActive = true
            this.changeOrderId = changeOrderId
        },
        openSignatureStatusModal(changeOrderId) {
            this.isSignatureStatusModalActive = true
            this.changeOrderId = changeOrderId
        },
        validateRemindBy(changeOrder) {
            if (changeOrder.status == 'draft' &&
                changeOrder.sent_at &&
                changeOrder.created_by == this.currentUserId &&
                changeOrder.remind_by &&
                moment(changeOrder.remind_by).isSameOrBefore(moment())) {
                this.customMessage
                    ('Change Order',
                        `Change order ${changeOrder.number} is not signed, do you want to send E-mail notification to Client and PM?`,
                        [
                            { text: 'Close' },
                            { text: 'Postpone' },
                            { text: 'Send Mail' }
                        ])
                    .then((e) => {
                        switch (e.button) {
                            case 0:
                                break
                            case 1:
                                this.openModalComponent(ChangeRemindBy, { entity: changeOrder, entity_type: 'change-order' })
                                break
                            case 2:
                                this.openMessageModal({ entity: changeOrder, entity_type: 'change-order', context: 'change-order-remind-message' })
                                break
                        }
                    })
            }
        }
    },
}
</script>
